import { useAuth0 } from "@auth0/auth0-react";
import { loggerService } from "./observability";
import { sharedEnvironment } from "@akina/config";
import { useLocaleStore, type  HomepageRedirectTarget, useHomepageRedirect } from "@akina/presentation";

export enum Tenant {
  Portal = "PORTAL",
  Care = "CARE",
}

export type AuthAppState = {
  returnTo: string;
};

const LOGOUT_PARAMS = {
  logoutParams: {
    returnTo: sharedEnvironment.auth0RedirectUri,
  },
};

const logoutCare = async (
  redirectTarget: HomepageRedirectTarget | null,
  setLogoutRedirectTarget: (target: HomepageRedirectTarget | null) => void,
  logout: ({ logoutParams }: { logoutParams: { returnTo: string } }) => Promise<void>,
) => {
  setLogoutRedirectTarget(redirectTarget);
  loggerService.info("logout, redirect target:", redirectTarget);
  // wait to allow the redirect target to be set before logging out
  await new Promise(resolve => setTimeout(resolve, 300));
  await logout(LOGOUT_PARAMS);
};

export const useAuth = () => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();
  const { locale } = useLocaleStore();
  const setLogoutRedirectTarget = useHomepageRedirect(state => state.setTarget);

  const authenticate = (type: "signup" | "login") => (appState: AuthAppState, email?: string, os?: string) =>
    loginWithRedirect({
      appState: appState,
      authorizationParams: {
        login_hint: email,
        screen_hint: type,
        ui_locales: locale,
        os: os,
      },
    });

  const refreshToken = async (route: string) => {
    await loginWithRedirect({
      appState: { returnTo: route },
    });
  };

  const appName = sharedEnvironment.appName;

  return {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    loginWithRedirect: authenticate("login"),
    refreshToken,
    logout: (
      { redirectTarget }: { redirectTarget: HomepageRedirectTarget | null } = {
        redirectTarget: null,
      },
    ) => (appName === "portal" ? logout(LOGOUT_PARAMS) : logoutCare(redirectTarget, setLogoutRedirectTarget, logout)),
    registerWithRedirect: authenticate("signup"),
  };
};
